import { LanguageUtils } from '@foundation/localization';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import type { PropsWithChildren } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import ServiceRegistrar, {
  TBootstrapMode,
} from '@/services/base/ServiceRegistrar';
import {
  getOptionalStartupableFailures,
  isStartupableStarted,
} from '@/services/base/StartupHelper';
import Logger, { LoggerOrigin } from '@/services/Logger';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';

import { LoadingSpinner } from './common';
import { ErrorHandler } from './common/errors/ErrorHandler';

interface IServiceBootstrapperProps extends PropsWithChildren {
  readonly bootstrapMode: TBootstrapMode;
}

export function ServiceBootstrapper({
  children,
  bootstrapMode,
}: IServiceBootstrapperProps) {
  const { accessToken, userId } = useAuth();
  const { i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasStarted = useRef(false);

  const [isReadyToRender, setReadyToRender] = useState<boolean>(false);
  const [startupError, setStartupError] = useState<unknown>();

  useEffect(() => {
    if (!accessToken || !userId) {
      return;
    }

    // Guard against StrictMode double-invocation — the nonce token API is single-use
    if (hasStarted.current) return;
    hasStarted.current = true;

    const serviceStartTime = Date.now();
    Logger.startup()
      .then(() => {
        const localeParam = LanguageUtils.language(
          searchParams.get('locale') ?? '',
        );
        if (localeParam) {
          i18n.changeLanguage(localeParam);
        }
      })
      .then(() => {
        if (searchParams.get('tc')?.toLowerCase() === 'true') {
          return TelemetryService.startup();
        }
      })
      .then(() =>
        ServiceRegistrar.startServicesFor(bootstrapMode, {
          accessToken,
          customerNumber: userId,
        }),
      )
      .then(() => {
        getOptionalStartupableFailures().forEach((failure) => {
          TelemetryService.trackException(failure.cause, 'bootstrapper', {
            serviceName: failure.serviceName,
          });
        });
      })
      .then(() => {
        const serviceStartupTime = Date.now() - serviceStartTime;

        Logger.info(
          LoggerOrigin.ReactComponent,
          `Services statup time was ${serviceStartupTime}ms`,
        );

        TelemetryService.trackMetric({
          name: 'StartupTime.Services',
          average: serviceStartupTime,
          sampleCount: 1,
        });
      })
      .then(() => {
        if (
          isStartupableStarted(PaymentStore) &&
          searchParams.has('nonceToken')
        ) {
          searchParams.set('pid', PaymentStore.paymentIntentId);
          searchParams.delete('nonceToken');
          setSearchParams(searchParams);
        }
      })
      .then(() => setReadyToRender(true))
      .catch((error) => {
        setStartupError(error);
        Logger.error(
          LoggerOrigin.ServiceBootstrapper,
          'Startup failure',
          error,
        );

        // Since we don't know at this stage which service failed to start,
        // we need to check if it survived before trying to send telemetry.
        if (isStartupableStarted(TelemetryService)) {
          TelemetryService.trackException(error, 'bootstrapper', {
            result: 'The application could not be started.',
          });
        }

        TelemetryService.trackException(
          new Error('Bootstrapping services failed.', { cause: error }),
          'ServiceBootstrapper.tsx',
        );
      });
  }, [bootstrapMode, accessToken, userId]);

  if (startupError) {
    return (
      <ErrorHandler
        err={startupError instanceof Error ? startupError : undefined}
      />
    );
  }

  if (!isReadyToRender) {
    return <LoadingSpinner />;
  }

  return <>{children}</>;
}
